// ./src/contexts/AppEssentialsContext.tsx
import React, { createContext, useContext } from "react";

import {
  useAppEssentials,
  UseAppEssentialsReturn,
  UseAppEssentialsOptions,
} from "@/hooks/useAppEssentials";

// ============================================
// Types
// ============================================

/**
 * The context value is exactly the hook's return type.
 * All lookups (by-ID, by-code, name getters, kind→category, glovebox,
 * maintenance/schedulable subsets) now live in the hook itself.
 */
export type AppEssentialsContextValue = UseAppEssentialsReturn;

export interface AppEssentialsProviderProps {
  children: React.ReactNode;
  options?: UseAppEssentialsOptions;
}

// ============================================
// Context
// ============================================

const AppEssentialsContext = createContext<AppEssentialsContextValue | undefined>(
  undefined
);

// ============================================
// Provider
// ============================================

export const AppEssentialsProvider: React.FC<AppEssentialsProviderProps> = ({
  children,
  options = { autoLoad: true },
}) => {
  const value = useAppEssentials(options);

  return (
    <AppEssentialsContext.Provider value={value}>
      {children}
    </AppEssentialsContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

/**
 * Hook to access app essentials (dictionary data) from context.
 * Must be used within AppEssentialsProvider.
 */
export const useAppEssentialsContext = (): AppEssentialsContextValue => {
  const context = useContext(AppEssentialsContext);

  if (context === undefined) {
    throw new Error(
      "useAppEssentialsContext must be used within an AppEssentialsProvider"
    );
  }

  return context;
};